import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { useState, useEffect } from "react";
import graphicon from "../../../assets/Dashboard/graphicon.svg";
import emptyscreen from "../../../assets/Dashboard/emptyscreen.svg";
import CustomText, { textStyles } from '../../../components/CustomText';
import {
  Box,
  useMediaQuery,
  Typography,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { showUnderDevelopment } from "../../../components/alerts";
import { useTranslation } from "react-i18next";
import { BASE_URL } from "../../../components/BaseUrl";
import { decryptToken } from "../../../Utlis/Encryption";

const MONTHS = [
  "Jan","Feb","Mar","Apr","May","Jun",
  "Jul","Aug","Sep","Oct","Nov","Dec",
];

const RequestGraph = () => {
  const isMobile = useMediaQuery("(max-width:768px)");
  const { t } = useTranslation();
  const [requestYear, setRequestYear] = useState(new Date().getFullYear());
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);

  const rawEncryptedToken = localStorage.getItem("token");
  const token = rawEncryptedToken ? decryptToken(rawEncryptedToken) : null;

  useEffect(() => {
    const controller = new AbortController();

    async function loadData() {
      try {
        setLoading(true);
        setChartData([]); // reset while loading
        const res = await fetch(
          `${BASE_URL}/account-executive/dashboard/company-stats?year=${requestYear}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            signal: controller.signal,
          }
        );
        if (!res.ok) throw new Error("Failed to fetch");
        const json = await res.json();

        if (!json.error) {
          const invited = json.data.monthly_breakdown.invited_companies;
          const requested = json.data.monthly_breakdown.requested_companies;

          const data = MONTHS.map((label, idx) => {
            const monthKey = Object.keys(invited)[idx];
            return {
              name: label,
              invitations: invited[monthKey],
              requests: requested[monthKey],
            };
          });

          // check if all values are zero
          const hasData = data.some(
            (d) => d.invitations > 0 || d.requests > 0
          );

          setChartData(hasData ? data : []);
        }
      } catch (err) {
        console.error(err);
        setChartData([]); // fallback empty
      } finally {
        setLoading(false);
      }
    }

    loadData();
    return () => controller.abort();
  }, [requestYear]);

  // Years list
  const years = Array.from({ length:100}, (_, i) => 2015 + i);
  const currentYear = new Date().getFullYear();

  return (
    <Box
      sx={{
        p: 2,
        backgroundColor: "white",
        border: "1px solid #091E4224",
        borderRadius: 2,
        boxShadow: "0px 0px 10px rgba(0,0,0,0.05)",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography
          sx={{
            fontSize: 15,
            fontFamily: "Nunito Sans, sans-serif",
            fontWeight: 700,
            color: "#212121",
          }}
        >
          {t("dashboard.request_vs_invitations")}
        </Typography>

        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          {/* ✅ Year Dropdown */}
          <Select
            size="small"
            value={requestYear}
            onChange={(e) => setRequestYear(e.target.value)}
            sx={{
              width: 90,
              fontSize: "13px",
              height: 32,
              "& .MuiSelect-select": {
                padding: "4px 8px",
                fontWeight: 600,
                textAlign: "center",
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: 200,
                  "& .MuiMenuItem-root": {
                    fontSize: "13px",
                    padding: "6px 10px",
                  },
                },
              },
            }}
          >
            {years.map((yr) => (
              <MenuItem
                key={yr}
                value={yr}
                disabled={yr > currentYear} // ✅ disable future years
              >
                {yr}
              </MenuItem>
            ))}
          </Select>

   
        </Box>
      </Box>

      {/* Content area: Loader / Empty / Chart */}
      <Box sx={{ minHeight: 300, display: "flex", justifyContent: "center", alignItems: "center" }}>
        {loading ? (
          <CircularProgress size={30} />
        ) : chartData.length === 0 ? (
     <Box sx={{ display: 'flex',flexDirection:"column" ,justifyContent: 'center', alignItems: 'center', height: 300 }}>
              <img src={emptyscreen} alt="No Data" style={{ width: 200,  }} />
                <CustomText
        sx={{
          ...textStyles.h1,
          fontWeight: 500,
          fontSize: '16px',
          color: '#003149',
          pt:1,
        }}
      >
      {t('empty.no_user')}
      </CustomText>
    
            </Box>  
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData} barSize={6} barGap={2} barCategoryGap={10}>
              <CartesianGrid vertical={false} stroke="#E0E0E0" />
              <XAxis
                dataKey="name"
                interval={0}
                tick={{
                  angle: isMobile ? -45 : 0,
                  fontSize: isMobile ? 11 : 13,
                  fill: "#2C384C",
                  fontWeight: 500,
                  fontFamily: "Poppins, sans-serif",
                }}
              />
              <YAxis
                tick={{ fontSize: 12, fill: "#030229" }}
                tickFormatter={(val) =>
                  val === 0 ? "0" : val >= 1000 ? `${val / 1000}K` : val
                }
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "transparent",
                  border: "none",
                  boxShadow: "none",
                }}
                cursor={{ fill: "transparent" }}
              />
              <Bar
                dataKey="invitations"
                name={t("dashboard.invitations")}
                fill="#1A7FFC"
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="requests"
                name={t("dashboard.requests")}
                fill="#FDCB47"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Box>
    </Box>
  );
};

export default RequestGraph;